import {
  Button,
  FieldLabel,
  FlexBox,
  FormField,
  FormGrid,
  Text,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import _groupBy from 'lodash/groupBy';
import moment from 'moment-timezone';

import type { OpeningHours } from 'components/powerFlowShared/components/nodeTypes/canvas/openingHours';

import { getBusinessHoursString } from '../../../../../businesshours/utils/hours';
import type { BusinessHourSchedule } from '../../../../types/businessHourSchedule';

type Props = {
  schedule: BusinessHourSchedule[];
  timezone: string;
  onEdit: VoidFunction;
};

const weekdays = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

type GroupedDays = Record<string, BusinessHourSchedule[]>;

function getGroupedDays(schedule: BusinessHourSchedule[]): GroupedDays {
  let index = 0;
  let currentKey = '';

  return _groupBy(schedule, (day: BusinessHourSchedule): string => {
    const intervalStr: string = getBusinessHoursString(day.intervals || []);
    const keyStr = `${index}-[${intervalStr}]`;
    if (keyStr !== currentKey) {
      index += 1;
      currentKey = `${index}-[${intervalStr}]`;
    }
    return currentKey;
  });
}

function getOpeningHours(schedule: BusinessHourSchedule[]) {
  const groupedDays = getGroupedDays(schedule);

  return Object.values(groupedDays).map<OpeningHours>((days) => {
    const [firstDay] = days;
    const dayNames = days.map(({ date }) => weekdays[new Date(date).getDay()]);
    const isOnlyWeekdays =
      dayNames.length === 5 &&
      weekdays.slice(1, -1).every((day, index) => day === dayNames[index]);
    const isOnlyWeekend =
      dayNames.length === 2 &&
      [weekdays[6], weekdays[0]].every((day, index) => day === dayNames[index]);
    const day = {} as OpeningHours;

    if (isOnlyWeekdays) {
      day.name = 'Weekdays';
    }

    if (isOnlyWeekend) {
      day.name = 'Weekends';
    }

    if (!day.name) {
      const { 0: firstDay, [dayNames.length - 1]: lastDay } = dayNames;

      day.name = dayNames.length === 1 ? firstDay : `${firstDay} - ${lastDay}`;
    }

    day.hours = getBusinessHoursString(firstDay.intervals || []);

    return day;
  });
}

export function Overview({ schedule, timezone, onEdit }: Props): JSX.Element {
  const openingHours = getOpeningHours(schedule);
  const timezoneOffset = moment.tz(timezone).format('Z');

  return (
    <FormGrid>
      <FormField>
        <FieldLabel>Timezone</FieldLabel>
        <FlexBox
          background="neutral.100"
          borderRadius="100"
          padding="300"
          margin="200 0 0"
        >
          <Text.Paragraph variant="2">
            {timezone} ({timezoneOffset})
          </Text.Paragraph>
        </FlexBox>
      </FormField>
      <FormField>
        <FieldLabel>Current business hours</FieldLabel>
        <FlexBox
          background="neutral.100"
          borderRadius="100"
          padding="300"
          margin="200 0 0"
          alignItems="center"
          justifyContent="space-between"
          gap="500"
        >
          <Text.Paragraph variant="2">
            {openingHours.map(({ name, hours }, index, arr) => (
              <span key={name}>
                {name}{' '}
                <strong>
                  {hours}
                  {index + 1 !== arr.length && ','}
                </strong>{' '}
              </span>
            ))}
          </Text.Paragraph>
          <Button
            variant="secondary"
            onClick={() => {
              ampli.businessHoursMigrationWizardEditHoursButtonClicked();
              onEdit();
            }}
          >
            Edit hours
          </Button>
        </FlexBox>
      </FormField>
    </FormGrid>
  );
}
